<template>
  <section class="passenger-picker">
    <div
      v-for="type in passengerTypes"
      :key="type.code"
      class="passenger-picker__counter"
      :class="{ 'passenger-picker__counter--filled': getPassengerCount(type) > 0 }">
      <div class="passenger-picker__row">
        <span class="passenger-picker__label">
          {{ $t(`descriptions.passengerNames.${type.code}`) }}
        </span>
        <div class="passenger-picker__controls">
          <button
            class="passenger-picker__button passenger-picker__button--minus"
            :class="{ 'passenger-picker__button--inactive': getPassengerCount(type) === 0 }"
            :disabled="getPassengerCount(type) === 0"
            @click="decrementPassenger(type)"
            type="button"
            :aria-label="$t('travelPlanner.passenger.removePassenger')">
            <svg>
              <use :xlink:href="`#${minus}`" />
            </svg>
          </button>
          <span
            class="passenger-picker__amount"
            :class="{ 'passenger-picker__amount--inactive': getPassengerCount(type) === 0 }">
            {{ getPassengerCount(type) }}
          </span>
          <button
            class="passenger-picker__button passenger-picker__button--plus"
            @click="incrementPassenger(type)"
            type="button"
            :aria-label="$t('travelPlanner.passenger.addPassenger')">
            <svg>
              <use :xlink:href="`#${plus}`" />
            </svg>
          </button>
        </div>
      </div>
      <PassengerTravelcard
        v-if="getPassengerCount(type) > 0"
        class="passenger-picker__travelcard"
        :passenger-type="type"
        :passengers="getPassengersByType(type)"
        @update-discount-card="handleDiscountCardUpdate" />
    </div>
  </section>
</template>

<script setup lang="ts">
  import { ref, onMounted, watch } from 'vue'
  import plus from '@/assets/icons/plus.svg'
  import minus from '@/assets/icons/minus.svg'
  import { useTravelSearchStore } from '@/stores/travelSearchStore'
  import { PassengerType, Passenger, DiscountCard, PASSENGER_TYPES } from '@/types/passengerTypes'
  import PassengerTravelcard from './PassengerTravelcard.vue'
  import { generatePassengerId } from '@/utils/passengerUrlUtils'
  import { pushInteractionToDatalayer } from '@/services/gaLoggingService'

  export interface Props {
    initialPassengers?: Passenger[]
  }

  const props = withDefaults(defineProps<Props>(), {
    initialPassengers: () => [],
  })

  const travelSearchStore = useTravelSearchStore()
  // Hardcoded (the full planner fetches these; see PASSENGER_TYPES), sorted by sequence.
  const passengerTypes = ref<PassengerType[]>(
    [...PASSENGER_TYPES].sort((a, b) => a.sequence_number - b.sequence_number),
  )
  const passengers = ref<Passenger[]>([])

  const getNextPassengerId = () => {
    const existingIds = passengers.value.map((p) => {
      const match = p.id.match(/passenger_(\d+)/)
      return match ? parseInt(match[1]) : 0
    })
    const highestId = Math.max(0, ...existingIds)
    return generatePassengerId(highestId)
  }

  const mapPassenger = (passenger: Passenger): Passenger => ({
    id: passenger.id || getNextPassengerId(),
    type: passenger.type,
    description:
      passenger.description ||
      passengerTypes.value.find((t) => t.code === passenger.type)?.description ||
      '',
    discount_cards: passenger.discount_cards || [],
  })

  onMounted(() => {
    if (props.initialPassengers && props.initialPassengers.length > 0) {
      passengers.value = props.initialPassengers.map(mapPassenger)
    } else if (travelSearchStore.passengers.length > 0) {
      passengers.value = travelSearchStore.passengers.map(mapPassenger)
    } else {
      const adultType = passengerTypes.value.find((type) => type.code === 'ADT')
      if (adultType) {
        passengers.value = [
          {
            id: getNextPassengerId(),
            type: adultType.code,
            description: adultType.description,
            discount_cards: [],
          },
        ]
      }
    }

    if (passengers.value.length > 0) {
      travelSearchStore.setPassengers([...passengers.value])
    }
  })

  watch(
    () => props.initialPassengers,
    (newPassengers) => {
      if (
        newPassengers &&
        newPassengers.length > 0 &&
        JSON.stringify(newPassengers) !== JSON.stringify(passengers.value)
      ) {
        passengers.value = newPassengers.map(mapPassenger)
      }
    },
    { deep: true },
  )

  watch(
    passengers,
    (newPassengers) => {
      if (JSON.stringify(newPassengers) !== JSON.stringify(travelSearchStore.passengers)) {
        travelSearchStore.setPassengers([...newPassengers])
      }
    },
    { deep: true },
  )

  const getPassengerCount = (type: PassengerType) => {
    return passengers.value.filter((p) => p.type === type.code).length
  }

  const getPassengersByType = (type: PassengerType) => {
    return passengers.value.filter((p) => p.type === type.code)
  }

  const incrementPassenger = (type: PassengerType) => {
    const newPassenger = {
      id: getNextPassengerId(),
      type: type.code,
      description: type.description,
      discount_cards: [],
    }
    passengers.value = [...passengers.value, newPassenger]
    pushInteractionToDatalayer('add', { selection: [type.code] })
  }

  const decrementPassenger = (type: PassengerType) => {
    const index = passengers.value.map((p) => p.type).lastIndexOf(type.code)
    if (index !== -1) {
      passengers.value = [...passengers.value.slice(0, index), ...passengers.value.slice(index + 1)]
      pushInteractionToDatalayer('remove', { selection: [type.code] })
    }
  }

  const handleDiscountCardUpdate = (passengerId: string, card: DiscountCard | null) => {
    passengers.value.forEach((passenger) => {
      if (passenger.id === passengerId) {
        passenger.discount_cards = card ? [card] : []
      }
    })
    travelSearchStore.setPassengers(passengers.value)
  }
</script>

<style lang="scss" scoped>
  .passenger-picker {
    @include body-md(400);
    display: flex;
    flex-direction: column;
    width: 100%;
    color: $clr-light-primary-900;

    &__counter {
      padding: rem(16);
      border-bottom: 1px solid $clr-stroke;

      &:last-child {
        border-bottom: none;
      }
    }

    &__row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: rem(36);
    }

    &__label {
      flex: 1 0 0;
      min-width: 0;
    }

    &__controls {
      display: flex;
      align-items: center;
      gap: rem(12);
      flex-shrink: 0;
    }

    &__amount {
      @include body-md(500);
      min-width: rem(12);
      text-align: center;
      color: $clr-primary;

      &--inactive {
        color: $clr-primary-200;
      }
    }

    &__button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: rem(22);
      height: rem(22);
      padding: 0;
      border-radius: 50%;
      border: 1px solid;
      box-shadow: 0 0 rem(2) rgba($clr-primary, 0.25);
      cursor: pointer;
      touch-action: manipulation;

      svg {
        width: rem(18);
        height: rem(18);
        fill: currentColor;
      }

      // Add: filled navy circle with a white icon.
      &--plus {
        background-color: $clr-primary;
        border-color: $clr-primary;
        color: $clr-white;
      }

      // Remove: outlined circle on white with a muted icon.
      &--minus {
        background-color: $clr-white;
        border-color: $clr-primary-50;
        color: $clr-primary-200;
      }

      &--inactive {
        cursor: not-allowed;
      }
    }

    // When a type has passengers, the travelcard area sits under the counter row
    // separated by a hairline.
    &__travelcard {
      margin-top: rem(12);
      border-top: 1px solid $clr-gray-100;
    }
  }
</style>
